/**
 * ProductDrawer — product detail + admin actions (STORY-008-007). `BottomSheet`
 * with the product profile and permission-gated actions:
 *   - Edit   (marketplace.product.edit)   → navigate to the shared product
 *     editor route (`/admin/products/{id}/edit`, Task F4)
 *   - Delete (marketplace.product.delete) → confirm dialog → DELETE (soft-delete)
 *
 * The category tree is consumed READ-ONLY (cloned `useCategoryTree`) to
 * resolve the displayed category name — category management itself is OUT
 * of M1 (D7).
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import {
  categoryDisplayName,
  useCategoryTree,
} from "@/features/marketplace/useCategoryTree";
import {
  formatKgs,
  formatKgsRange,
  kgsLocaleForLanguage,
} from "@/lib/format-currency";
import { parseDecimal } from "@/lib/format";
import { productStatusTone, stockTone } from "@/features/admin/components/tone";
import { useProduct, useSoftDeleteProduct } from "@/features/admin/products/hooks";

export interface ProductDrawerProps {
  productId: string | null;
  onClose: () => void;
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <dt className="text-xs font-medium text-[var(--g-secondary)]">{label}</dt>
      <dd className="text-sm">{value ?? "—"}</dd>
    </div>
  );
}

export function ProductDrawer({
  productId,
  onClose,
}: ProductDrawerProps): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: product, isLoading } = useProduct(productId);
  const { findNode } = useCategoryTree();
  const del = useSoftDeleteProduct();

  const canEdit = useRequirePermission("marketplace.product.edit");
  const canDelete = useRequirePermission("marketplace.product.delete");

  useEffect(() => {
    if (productId === null) {
      setConfirmDelete(false);
    }
  }, [productId]);

  const isOpen = productId !== null;
  const locale = kgsLocaleForLanguage(i18n.language);

  // A BAND is quoted as a band. `price_kgs` is only the LOW end when
  // `price_max_kgs` is set (v0052), so quoting it alone states 650 for a
  // product that sells at 650-1 000 — the same defect the chat card and the
  // marketplace grid were already fixed for.
  const priceLabel = (raw: string | null, max?: string | null): string => {
    if (raw == null) return "—";
    const low = parseDecimal(raw);
    const high = max == null ? null : parseDecimal(max);
    return high !== null && high > low
      ? formatKgsRange(low, high, locale)
      : formatKgs(low, locale);
  };

  const categoryName = (id: string): string => {
    const node = findNode(id);
    return node ? categoryDisplayName(node, i18n.language) : id.slice(0, 8);
  };

  const handleDelete = (): void => {
    if (!product) return;
    del.mutate(product.id, {
      onSuccess: () => {
        toast.success(t("products.delete.success"));
        setConfirmDelete(false);
        onClose();
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  return (
    <>
      <BottomSheet
        open={isOpen}
        onClose={onClose}
        title={t("products.detail.title")}
        desktop={isDesktop}
      >
        {isLoading || !product ? (
          <div className="flex flex-col gap-3 py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div data-testid="product-detail" className="flex flex-col gap-4 overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2">
              <GPill tone={productStatusTone(product.status)} size="sm">
                {t(`products.status.${product.status}`, { defaultValue: product.status })}
              </GPill>
              <GPill tone={stockTone(product.stock_state)} size="sm">
                {t(`products.stock.${product.stock_state}`, {
                  defaultValue: product.stock_state,
                })}
              </GPill>
            </div>

            <dl className="divide-y divide-[var(--g-hairline)]">
              <FieldRow label={t("products.detail.name")} value={product.name} />
              <FieldRow label={t("products.detail.category")} value={categoryName(product.category_id)} />
              <FieldRow label={t("products.detail.price")} value={priceLabel(product.price_kgs, product.price_max_kgs)} />
              <FieldRow label={t("products.detail.bulkPrice")} value={priceLabel(product.bulk_price_kgs)} />
              <FieldRow
                label={t("products.detail.bulkThreshold")}
                value={product.bulk_threshold != null ? String(product.bulk_threshold) : "—"}
              />
              <FieldRow label={t("products.detail.seller")} value={product.seller_id.slice(0, 8)} />
              <FieldRow label={t("products.detail.description")} value={product.description} />
            </dl>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("products.detail.actions")}
              </h3>
              <div className="flex flex-wrap gap-2">
                {canEdit ? (
                  <Button
                    data-testid="product-action-edit"
                    variant="primary"
                    size="md"
                    onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                    className="min-h-[44px]"
                  >
                    {t("products.action.edit")}
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    data-testid="product-action-delete"
                    variant="destructive"
                    size="md"
                    onClick={() => setConfirmDelete(true)}
                    className="min-h-[44px]"
                  >
                    {t("products.action.delete")}
                  </Button>
                ) : null}
              </div>
            </section>
          </div>
        )}
      </BottomSheet>

      {/* Delete confirm — destructive, separated from the primary edit flow. */}
      <BottomSheet
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("products.delete.confirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("products.delete.confirmBody")}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(false)}
              disabled={del.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="product-delete-confirm"
              loading={del.isPending}
              onClick={handleDelete}
              className="min-h-[44px]"
            >
              {t("products.delete.confirm")}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}

export default ProductDrawer;
